import { getAttributeDataSourceByKey } from '@gonfalon/openapi/operations/getAttributeDataSourceByKey';
import { getAttributeDataSources } from '@gonfalon/openapi/operations/getAttributeDataSources';
import { patchAttributeDataSource } from '@gonfalon/openapi/operations/patchAttributeDataSource';
import { postAttributeDataSource } from '@gonfalon/openapi/operations/postAttributeDataSource';
import type { AttributeDataSourceRep } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';

export const getAttributeDataSourcesQuery = createQueryOptions(getAttributeDataSources);

export const getAttributeDataSourceByKeyQuery = createQueryOptions(getAttributeDataSourceByKey);

export const useCreateAttributeDataSource = createMutationHook(postAttributeDataSource, {
  onSuccess: (data, input) => {
    const queryClient = getQueryClient();

    queryClient.setQueryData(
      getAttributeDataSourceByKeyQuery({ projKey: input.projKey, dataSourceKey: data.key }).queryKey,
      data,
    );

    void queryClient.invalidateQueries({
      queryKey: getAttributeDataSourcesQuery.partialQueryKey({
        projKey: input.projKey,
      }),
    });
  },
});

type AttributeDataSourceGetExpansions = Pick<
  AttributeDataSourceRep,
  'experimentKeys' | 'experimentsCount' | '_integrationConfigurationSummary'
>;

function mergeAttributeDataSourceGetExpansions<T extends AttributeDataSourceGetExpansions>(
  revision: T,
  previous: T | undefined,
): T {
  if (!previous) {
    return revision;
  }

  return {
    ...revision,
    experimentKeys: revision.experimentKeys ?? previous.experimentKeys,
    experimentsCount: revision.experimentsCount ?? previous.experimentsCount,
    _integrationConfigurationSummary:
      revision._integrationConfigurationSummary ?? previous._integrationConfigurationSummary,
  };
}

export const useUpdateAttributeDataSource = createMutationHook(patchAttributeDataSource, {
  onSuccess: (data, input) => {
    const queryClient = getQueryClient();

    queryClient.setQueryData(
      getAttributeDataSourceByKeyQuery({ projKey: input.projKey, dataSourceKey: input.dataSourceKey }).queryKey,
      (previous: typeof data | undefined) => mergeAttributeDataSourceGetExpansions(data, previous),
    );

    void queryClient.invalidateQueries({
      queryKey: getAttributeDataSourcesQuery.partialQueryKey({
        projKey: input.projKey,
      }),
    });
  },
});
